const AES_KEY = "c4312b5bf6b2dabf3fe54396b8bfe7c0";
const RESPONSE_KEY = "482a7567f75b83294277b9366ed2a773";
const CRYPTO_JS_CDN = "https://cdnjs.cloudflare.com/ajax/libs/crypto-js/4.1.1/crypto-js.min.js";
/** 关闭地区选择器后的冷却时间戳；实际 key 为前缀 + 产品 id（或代际），见 getRegionSelectorClosedStorageKey */
const REGION_SELECTOR_CLOSED_AT_KEY_PREFIX = "giftRedemptionRegionSelectorClosedAt";
const GIFT_REDEMPTION_CACHE_KEY_PREFIX = "giftRedemptionFlow";
const GIFT_REDEMPTION_CHECKOUT_REDIRECT_KEY = `${GIFT_REDEMPTION_CACHE_KEY_PREFIX}:checkoutRedirect`;
/** Pad/Mob：点击展开的帮助气泡在约此时长后自动收起（兼顾阅读与不打断操作） */
const TOOLTIP_TOUCH_AUTO_HIDE_MS = 6000;

class GiftRedemptionFlow extends HTMLElement {
  static _checkoutReturnListenerBound = false;

  static ensureCheckoutReturnListener() {
    if (GiftRedemptionFlow._checkoutReturnListenerBound) return;
    GiftRedemptionFlow._checkoutReturnListenerBound = true;

    window.addEventListener("pageshow", () => {
      document.querySelector("[data-terms-checkbox]").checked = false;
    });
  }

  connectedCallback() {
    this._tooltipAutoHideTimer = null;
    this.state = {
      currentStep: 1,
      redemption: null,
      discountCode: "",
      checkoutVariantId: "",
      selectedOptions: {},
      selectedVariant: null,
      productReady: false,
      codeVisible: false,
      completedSteps: {
        1: false,
        2: false,
      },
      fields: {
        serialNumber: "",
        redemptionCode: "",
        email: "",
      },
    };

    try {
      window.localStorage.removeItem("giftRedemptionRegionSelectorOpenedAt");
      window.localStorage.removeItem("giftRedemptionRegionSelectorClosedAt");
    } catch (error) {
      // Ignore storage errors (legacy global keys; now per-product keys with suffix).
    }

    this.cacheElements();
    this.restoreCachedFields();
    this.populateFieldsFromUrl();
    this.setProductReady(false);
    this.bindEvents();
    this.bindCheckoutReturnReset();
    this._onRegionSelectorDocumentClosed = (event) => {
      const root = event.detail?.selectorElement;
      if (!root || !this.contains(root)) return;
      this.markRegionSelectorClosed();
    };
    document.addEventListener("region-selector:closed", this._onRegionSelectorDocumentClosed);
    this.initializeProduct().then(() => {
      this.handleCheckoutReturn();
    });
  }

  disconnectedCallback() {
    if (this._onRegionSelectorDocumentClosed) {
      document.removeEventListener("region-selector:closed", this._onRegionSelectorDocumentClosed);
    }
  }

  readProduct() {
    const script = this.querySelector("[data-redemption-product-json]");
    if (!script) return null;

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.error("Failed to parse redemption product JSON", error);
      return null;
    }
  }

  async initializeProduct() {
    this.product = this.readProduct();

    if (!this.product) {
      this.showMessage(
        this.redemptionMessage,
        this.text(
          "productMissingText",
          "Product information is missing. Please check the page URL or configure a fallback product.",
        ),
        "error",
      );
      this.setProductReady(false);
      return;
    }

    this.dataset.productId = this.product.id;
    this.dataset.productTitle = this.product.title;
    this.renderProduct();
    this.restoreCachedOptions();
    this.initializeSelection();
    this.setProductReady(true);
    this.syncVariant();
    this.restoreCachedProgress();
    this.openInitialRegionSelector();
  }

  /** 第一步未完成时可用礼品卡 query 覆盖缓存；已 verify 则仅以缓存为准，忽略 URL */
  hasCachedVerifiedBinding() {
    return Boolean(this.readCache().progress?.verified);
  }

  populateFieldsFromUrl() {
    if (this.hasCachedVerifiedBinding()) return;

    const params = new URLSearchParams(window.location.search);
    const fieldParams = {
      serialNumber: ["giftCardSn", "serial_number", "serialNumber", "sn"],
      redemptionCode: ["giftCardCode", "redemption_code", "redemptionCode", "redeemCode"],
      email: ["giftCardEmail", "email", "giftCardEmailAddress"],
    };

    Object.entries(fieldParams).forEach(([key, names]) => {
      const value = names.map((name) => params.get(name)).find(Boolean);
      if (!value || !this.inputs?.[key]) return;

      this.inputs[key].value = value;
      this.state.fields[key] = value;
    });

    this.cacheFields();
  }

  /** 与 populateFieldsFromUrl 一致的礼品卡 query 参数，取消绑定后去掉以免刷新又回填 */
  stripGiftCardUrlParams() {
    const paramNames = [
      "giftCardSn",
      "serial_number",
      "serialNumber",
      "sn",
      "giftCardCode",
      "redemption_code",
      "redemptionCode",
      "redeemCode",
      "giftCardEmail",
      "email",
      "giftCardEmailAddress",
    ];
    const params = new URLSearchParams(window.location.search);
    let changed = false;
    for (const key of paramNames) {
      if (params.has(key)) {
        params.delete(key);
        changed = true;
      }
    }
    if (!changed) return;
    const query = params.toString();
    const url = `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`;
    window.history.replaceState(null, "", url);
  }

  getCacheKey() {
    return `${GIFT_REDEMPTION_CACHE_KEY_PREFIX}:${this.dataset.productGeneration || "default"}`;
  }

  /** 与当前配置产品绑定，不同兑换商品的地区弹窗冷却互不影响 */
  getRegionSelectorClosedStorageKey() {
    const productId = String(this.dataset.productId || "").trim();
    if (productId) return `${REGION_SELECTOR_CLOSED_AT_KEY_PREFIX}:${productId}`;
    return `${REGION_SELECTOR_CLOSED_AT_KEY_PREFIX}:gen-${this.dataset.productGeneration || "default"}`;
  }

  readCache() {
    try {
      return JSON.parse(window.localStorage.getItem(this.getCacheKey()) || "{}");
    } catch (error) {
      return {};
    }
  }

  writeCache(partial) {
    try {
      window.localStorage.setItem(
        this.getCacheKey(),
        JSON.stringify({
          ...this.readCache(),
          ...partial,
          updatedAt: Date.now(),
        }),
      );
    } catch (error) {
      // Ignore storage errors in private browsing or restricted environments.
    }
  }

  clearCache() {
    try {
      window.localStorage.removeItem(this.getCacheKey());
    } catch (error) {
      // Ignore storage errors in private browsing or restricted environments.
    }
  }

  restoreCachedFields() {
    const fields = this.readCache().fields || {};

    Object.entries(fields).forEach(([key, value]) => {
      if (!value || !this.inputs?.[key]) return;

      this.inputs[key].value = value;
      this.state.fields[key] = value;
    });
  }

  cacheFields() {
    const fields = {
      serialNumber: this.inputs.serialNumber?.value.trim() || "",
      redemptionCode: this.inputs.redemptionCode?.value.trim() || "",
      email: this.inputs.email?.value.trim() || "",
    };

    this.state.fields = fields;
    this.writeCache({ fields });
  }

  restoreCachedOptions() {
    const selectedOptions = this.readCache().selectedOptions || {};
    if (!Object.keys(selectedOptions).length) return;

    this.optionButtons.forEach((button) => {
      const selectedValue = selectedOptions[button.dataset.optionPosition];
      if (!selectedValue) return;

      const isSelected = selectedValue && button.dataset.optionValue === selectedValue;

      button.classList.toggle("is-selected", Boolean(isSelected));
      button.setAttribute("aria-pressed", isSelected ? "true" : "false");
    });
  }

  cacheSelectedOptions() {
    this.writeCache({ selectedOptions: this.state.selectedOptions });
  }

  cacheProgress(partial) {
    this.writeCache({
      progress: {
        ...(this.readCache().progress || {}),
        ...partial,
      },
    });
  }

  restoreCachedProgress() {
    const progress = this.readCache().progress || {};
    if (!progress.verified) return;

    this.applyVerifiedState({});
    this.syncVariant();
    if (progress.confirmed && this.state.selectedVariant?.available) {
      this.applyConfirmedState();
      return;
    }

    this.cacheProgress({ confirmed: false });
  }

  renderProduct() {
    if (!this.productImage && this.productMedia) {
      this.productMedia.innerHTML = "";
      this.productImage = document.createElement("img");
      this.productImage.className = "gift-redemption-flow__product-image";
      this.productImage.dataset.redemptionProductImage = "";
      this.productImage.loading = "lazy";
      this.productMedia.append(this.productImage);
    }

    if (!this.summaryImage && this.summaryMedia) {
      Array.from(this.summaryMedia.children).forEach((child) => {
        if (child !== this.summaryDefaultRing) child.remove();
      });
      this.summaryImage = document.createElement("img");
      this.summaryImage.className = "gift-redemption-flow__summary-image";
      this.summaryImage.dataset.summaryImage = "";
      this.summaryImage.loading = "lazy";
      this.summaryMedia.append(this.summaryImage);
    }

    this.updateImage(this.productImage, this.product.featuredImage);
    this.updateImage(this.summaryImage, this.product.featuredImage);
    if (this.summary?.title) this.summary.title.textContent = this.product.title;

    const productOptionsContainer = this.querySelector(".gift-redemption-flow__product-options");
    if (!productOptionsContainer || !this.product.options.length) return;

    productOptionsContainer.querySelectorAll("[data-option-fieldset]").forEach((fieldset) => fieldset.remove());

    const colorOption = this.product.options.find((option) => /colou?r/i.test(option.name)) || this.product.options[0];
    const sizeOption = this.product.options.find((option) => /size/i.test(option.name)) || this.product.options[1];
    const defaultVariant = this.getDefaultVariant();
    const fragment = document.createDocumentFragment();

    if (colorOption) {
      fragment.append(this.buildOptionFieldset(colorOption, "color", defaultVariant));
    }

    if (sizeOption && sizeOption !== colorOption) {
      fragment.append(this.buildOptionFieldset(sizeOption, "size", defaultVariant));
    }

    productOptionsContainer.insertBefore(fragment, this.confirmButton || this.productMessage);
    this.optionButtons = Array.from(this.querySelectorAll("[data-option-button]"));
    this.selectedColorLabel = productOptionsContainer.querySelector("[data-selected-color]");
    this.optionButtons.forEach((button) => {
      button.addEventListener("click", () => this.selectOption(button));
    });
  }

  buildOptionFieldset(option, type, defaultVariant) {
    const fieldset = document.createElement("fieldset");
    fieldset.className = "gift-redemption-flow__option";
    fieldset.dataset.optionFieldset = "";
    fieldset.dataset.optionPosition = option.position;
    fieldset.innerHTML = `
      <legend>
        <span>${this.escapeHtml(option.name)}</span>
        ${type === "color" ? `<strong data-selected-color>${this.escapeHtml(defaultVariant?.[`option${option.position}`] || option.values[0] || "")}</strong>` : ""}
      </legend>
      <div class="gift-redemption-flow__swatches gift-redemption-flow__swatches--${type}"></div>
    `;

    const swatches = fieldset.querySelector(".gift-redemption-flow__swatches");
    let visibleIndex = 0;

    option.values.forEach((value, index) => {
      if (type === "size" && this.isHiddenSizeValue(value)) return;
      const isDefaultSelected = type === "color" && defaultVariant?.[`option${option.position}`] === value;

      const button = document.createElement("button");
      button.type = "button";
      button.className = `gift-redemption-flow__swatch gift-redemption-flow__swatch--${type}${isDefaultSelected || (type === "color" && !defaultVariant && visibleIndex === 0) ? " is-selected" : ""}`;
      button.dataset.optionButton = "";
      button.dataset.optionPosition = option.position;
      button.dataset.optionValue = value;
      button.setAttribute("aria-label", value);
      button.setAttribute(
        "aria-pressed",
        isDefaultSelected || (type === "color" && !defaultVariant && visibleIndex === 0) ? "true" : "false",
      );

      if (this.isNewBadgeValue(value)) {
        const badge = document.createElement("span");
        badge.className = "gift-redemption-flow__new-badge";
        badge.textContent = this.dataset.newBadgeText || "New";
        button.append(badge);
      }

      const content = document.createElement("span");
      if (type === "color") {
        content.setAttribute("style", this.getColorSwatchStyle(value));
      } else {
        content.textContent = value;
      }
      button.append(content);
      swatches.append(button);
      visibleIndex += 1;
    });

    return fieldset;
  }

  getDefaultVariant() {
    return this.product.variants.find((variant) => variant.available) || this.product.variants[0] || null;
  }

  isHiddenSizeValue(value) {
    const raw = value == null ? "" : String(value);
    const normalizedValue = raw.toLowerCase();
    return (
      normalizedValue.includes("don't know") ||
      normalizedValue.includes("dont know") ||
      normalizedValue.includes("not sure my size") ||
      normalizedValue.includes("sizing kit") ||
      normalizedValue.includes("size kit")
    );
  }

  isNewBadgeValue(value) {
    const normalizedValue = value.toLowerCase().trim();
    return (this.dataset.newBadgeValues || "")
      .split(",")
      .map((item) => item.trim().toLowerCase())
      .filter(Boolean)
      .includes(normalizedValue);
  }

  /** 与商品页 `snippets/swatch.liquid` 同源：服务端按 settings.color_swatch_config 预解析每条颜色值 */
  getColorSwatchStyleMap() {
    if (this._colorSwatchStyleMap !== undefined) return this._colorSwatchStyleMap;
    const el = this.querySelector("[data-grf-color-swatch-styles]");
    if (!el?.textContent?.trim()) {
      this._colorSwatchStyleMap = null;
      return null;
    }
    try {
      this._colorSwatchStyleMap = JSON.parse(el.textContent);
    } catch {
      this._colorSwatchStyleMap = null;
    }
    return this._colorSwatchStyleMap;
  }

  getColorSwatchStyle(value) {
    const key = String(value ?? "").trim();
    const map = this.getColorSwatchStyleMap();
    if (map && typeof map[key] === "string" && map[key].length) {
      // swatch.liquid 在配置未命中时用 option 文案凑 gradient，中文会得到非法 CSS；此时走下方 colorMap
      if (!/linear-gradient\([^)]*[\u4e00-\u9fff]/.test(map[key])) {
        return map[key];
      }
    }

    const normalizedValue = key.toLowerCase();
    const themeSwatch = this.getThemeColorSwatch(normalizedValue);

    if (themeSwatch) {
      return `--swatch-background: ${themeSwatch}`;
    }

    const colorMap = {
      "brushed silver": "#c9c9c9",
      silver: "#c9c9c9",
      "future silver": "#c9c9c9",
      "royal gold": "#dfb98e",
      "dune gold": "#dfb98e",
      gold: "#dfb98e",
      "rose gold": "#f3c3a0",
      "matte black": "#242626",
      black: "#242626",
      "midnight black": "#242626",
      /* 中文选项名：Shopify 变体为中文时 downcase 仍与原字符串一致，需单独映射 */
      皇家金: "#dfb98e",
      帝王金: "#dfb98e",
      沙丘金: "#dfb98e",
      未来之银: "#c9c9c9",
      磨砂银: "#c9c9c9",
      拉丝银: "#c9c9c9",
      刷纹银: "#c9c9c9",
      经典银: "#c9c9c9",
      星尘银: "#c9c9c9",
      银色: "#c9c9c9",
      哑光黑色: "#242626",
      午夜黑: "#242626",
      魅夜黑: "#242626",
      魅力黑: "#242626",
      玫瑰金: "#f3c3a0",
      粉金: "#f3c3a0",
    };
    const color = colorMap[normalizedValue] || "#e8e8e8";
    return `--swatch-background: ${color}`;
  }

  getThemeColorSwatch(normalizedValue) {
    return (this.dataset.colorSwatchConfig || "")
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean)
      .reduce((matchedColor, line) => {
        if (matchedColor) return matchedColor;

        const separatorIndex = line.indexOf(":");
        if (separatorIndex === -1) return "";

        const name = line.slice(0, separatorIndex).trim().toLowerCase();
        const color = line.slice(separatorIndex + 1).trim();

        return name === normalizedValue ? color : "";
      }, "");
  }

  escapeHtml(value) {
    return String(value || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  escapeAttribute(value) {
    return this.escapeHtml(value);
  }

  setProductReady(isReady) {
    this.state.productReady = isReady;
    this.classList.toggle("is-product-loading", !isReady);

    if (this.verifyButton) {
      this.verifyButton.toggleAttribute("aria-busy", !isReady);
      this.updateVerifyButtonState();
    }

    if (!isReady && this.confirmButton) {
      this.confirmButton.disabled = true;
    }
  }

  cacheElements() {
    this.verifyButton = this.querySelector("[data-verify-button]");
    if (this.verifyButton) {
      this.verifyButton.dataset.defaultText = this.verifyButton.querySelector("span")?.textContent || "Verify";
    }
    this.cancelButton = this.querySelector("[data-cancel-button]");
    this.confirmButton = this.querySelector("[data-confirm-button]");
    if (this.confirmButton) {
      this.confirmButton.dataset.defaultText = this.confirmButton.querySelector("span")?.textContent || "Confirm";
    }
    this.checkoutButton = this.querySelector("[data-checkout-button]");
    this.termsCheckbox = this.querySelector("[data-terms-checkbox]");
    this.codeToggle = this.querySelector("[data-code-toggle]");
    this.productPanel = this.querySelector("[data-product-panel]");
    this.productMedia = this.querySelector(".gift-redemption-flow__product-media");
    this.summaryMedia = this.querySelector(".gift-redemption-flow__summary-media");
    this.summaryDefaultRing = this.querySelector(".gift-redemption-flow__summary-default-ring");
    this.productImage = this.querySelector("[data-redemption-product-image]");
    this.summaryImage = this.querySelector("[data-summary-image]");
    this.selectedColorLabel = this.querySelector("[data-selected-color]");
    this.redemptionMessage = this.querySelector("[data-redemption-message]");
    this.productMessage = this.querySelector("[data-product-message]");
    this.checkoutMessage = this.querySelector("[data-checkout-message]");
    this.regionSelectorTrigger = this.querySelector(
      ".gift-redemption-flow__region-selector [data-region-popover-trigger]",
    );
    this.optionButtons = Array.from(this.querySelectorAll("[data-option-button]"));
    this.tooltipButtons = Array.from(this.querySelectorAll("[data-tooltip-toggle]"));
    this.inputs = {
      serialNumber: this.querySelector('[name="serial_number"]'),
      redemptionCode: this.querySelector('[name="redemption_code"]'),
      email: this.querySelector('[name="email"]'),
    };
    this.summary = {
      title: this.querySelector("[data-summary-product-title]"),
      variantTitle: this.querySelector("[data-summary-variant-title]"),
      serial: this.querySelector("[data-summary-serial]"),
      code: this.querySelector("[data-summary-code]"),
      email: this.querySelector("[data-summary-email]"),
    };
  }

  initializeSelection() {
    this.state.selectedOptions = {};

    this.optionButtons.forEach((button) => {
      if (button.classList.contains("is-selected")) {
        this.state.selectedOptions[button.dataset.optionPosition] = button.dataset.optionValue;
      }
    });
  }

  bindEvents() {
    this.verifyButton?.addEventListener("click", () => this.verifyRedemption());
    this.cancelButton?.addEventListener("click", () => this.cancelRedemption());
    this.confirmButton?.addEventListener("click", () => this.confirmSelection());
    this.checkoutButton?.addEventListener("click", () => this.goToCheckout());
    this.termsCheckbox?.addEventListener("change", () => {
      this.checkoutButton.disabled = !this.termsCheckbox.checked || !this.state.checkoutVariantId;
    });
    this.codeToggle?.addEventListener("click", () => this.toggleCodeVisibility());

    this.optionButtons.forEach((button) => {
      button.addEventListener("click", () => this.selectOption(button));
    });

    Object.entries(this.inputs).forEach(([key, input]) => {
      input?.addEventListener("input", () => {
        this.clearFieldError(this.inputKeyToFieldName(key));
        this.cacheFields();
        this.updateVerifyButtonState();
      });
      input?.addEventListener("blur", () => {
        this.validateFieldOnBlur(key);
        this.updateVerifyButtonState();
      });
    });

    this.tooltipButtons.forEach((button) => {
      button.addEventListener("click", (event) => this.toggleTooltip(event, button));
    });

    document.addEventListener("click", (event) => {
      if (!this.contains(event.target)) {
        this.closeTooltips();
      }
    });
    window.addEventListener("resize", () => {
      this.closeTooltips();
      this.updateProgressLine();
    });
  }

  bindCheckoutReturnReset() {
    GiftRedemptionFlow.ensureCheckoutReturnListener();
  }

  markLeavingForCheckout() {
    this._awaitingCheckoutReturn = true;
    this.markCheckoutRedirect();
  }

  hasCheckoutRedirectFlag() {
    try {
      return window.sessionStorage.getItem(GIFT_REDEMPTION_CHECKOUT_REDIRECT_KEY) === "1";
    } catch (error) {
      return false;
    }
  }

  handleCheckoutReturn() {
    if (!this._awaitingCheckoutReturn && !this.hasCheckoutRedirectFlag()) return;

    this._awaitingCheckoutReturn = false;
    this.clearCheckoutRedirectFlag();
    this.resetCheckoutStep();
  }

  clearInputFields() {
    try {
      ["serialNumber", "redemptionCode", "email"].forEach((fieldName) => {
        const input = this.inputs?.[fieldName];
        if (!input) return;

        input.value = "";
        input.disabled = false;
      });
    } catch (error) {
      // ignore field resetting errors
    }

    this.state.fields = {
      serialNumber: "",
      redemptionCode: "",
      email: "",
    };
    this.clearFieldError("serial_number");
    this.clearFieldError("redemption_code");
    this.clearFieldError("email");
    this.updateVerifyButtonState();
  }

  markCheckoutRedirect() {
    try {
      window.sessionStorage.setItem(GIFT_REDEMPTION_CHECKOUT_REDIRECT_KEY, "1");
    } catch (error) {
      // Ignore storage errors.
    }
  }

  clearCheckoutRedirectFlag() {
    try {
      window.sessionStorage.removeItem(GIFT_REDEMPTION_CHECKOUT_REDIRECT_KEY);
    } catch (error) {
      // Ignore storage errors.
    }
  }

  openInitialRegionSelector() {
    if (!this.regionSelectorTrigger || this.shouldSkipInitialRegionSelector()) return;

    window.requestAnimationFrame(() => {
      if (!this.regionSelectorTrigger) return;
      this.regionSelectorTrigger.click();
    });
  }

  shouldSkipInitialRegionSelector() {
    return this.hasRegionSelectorSkipParam() || this.hasRecentRegionSelectorOpen();
  }

  hasRegionSelectorSkipParam() {
    const params = new URLSearchParams(window.location.search);
    const skipParamNames = (this.dataset.regionSelectorSkipParams || "")
      .split(",")
      .map((name) => name.trim())
      .filter(Boolean);

    return skipParamNames.some((name) => params.has(name));
  }

  getRegionSelectorCooldownMs() {
    const minutes = Number(this.dataset.regionSelectorCooldownMinutes);
    const safe = Number.isFinite(minutes) && minutes > 0 ? minutes : 1440;
    return safe * 60 * 1000;
  }

  hasRecentRegionSelectorOpen() {
    try {
      const closedAt = Number(window.localStorage.getItem(this.getRegionSelectorClosedStorageKey()) || 0);
      return closedAt > 0 && Date.now() - closedAt < this.getRegionSelectorCooldownMs();
    } catch (error) {
      return false;
    }
  }

  markRegionSelectorClosed() {
    try {
      window.localStorage.setItem(this.getRegionSelectorClosedStorageKey(), String(Date.now()));
    } catch (error) {
      // Ignore storage errors in private browsing or restricted environments.
    }
  }

  toggleTooltip(event, button) {
    if (!window.matchMedia("(max-width: 1279px)").matches) return;

    event.preventDefault();
    event.stopPropagation();

    const willOpen = !button.classList.contains("is-open");
    this.closeTooltips();
    button.classList.toggle("is-open", willOpen);
    button.setAttribute("aria-expanded", willOpen ? "true" : "false");
    if (willOpen) this.scheduleTooltipAutoHide();
  }

  scheduleTooltipAutoHide() {
    if (this._tooltipAutoHideTimer != null) {
      window.clearTimeout(this._tooltipAutoHideTimer);
      this._tooltipAutoHideTimer = null;
    }
    this._tooltipAutoHideTimer = window.setTimeout(() => {
      this._tooltipAutoHideTimer = null;
      this.closeTooltips();
    }, TOOLTIP_TOUCH_AUTO_HIDE_MS);
  }

  closeTooltips() {
    if (this._tooltipAutoHideTimer != null) {
      window.clearTimeout(this._tooltipAutoHideTimer);
      this._tooltipAutoHideTimer = null;
    }
    this.tooltipButtons.forEach((button) => {
      button.classList.remove("is-open");
      button.setAttribute("aria-expanded", "false");
      const tooltip = button.querySelector(".gift-redemption-flow__tooltip");
      if (!tooltip?.style) return;
      tooltip.style.removeProperty("--grf-tooltip-left");
      tooltip.style.removeProperty("--grf-tooltip-top");
      tooltip.style.removeProperty("--grf-tooltip-width");
    });
  }

  selectOption(button) {
    if (button.classList.contains("is-unavailable")) return;

    const { optionPosition, optionValue } = button.dataset;
    this.state.selectedOptions[optionPosition] = optionValue;

    this.optionButtons
      .filter((item) => item.dataset.optionPosition === optionPosition)
      .forEach((item) => {
        const isSelected = item === button;
        item.classList.toggle("is-selected", isSelected);
        item.setAttribute("aria-pressed", isSelected ? "true" : "false");
      });

    this.ensureAvailableSelection(optionPosition);
    this.cacheSelectedOptions();
    this.syncVariant();
  }

  ensureAvailableSelection(changedPosition) {
    const currentVariant = this.findMatchingVariant();
    if (currentVariant?.available) return;

    const availableVariant = this.product.variants.find((variant) => {
      if (!variant.available) return false;
      return variant[`option${changedPosition}`] === this.state.selectedOptions[String(changedPosition)];
    });

    if (!availableVariant) return;

    this.product.options.forEach((option) => {
      const value = availableVariant[`option${option.position}`];
      if (value) {
        this.state.selectedOptions[String(option.position)] = value;
      }
    });

    this.optionButtons.forEach((button) => {
      const selectedValue = this.state.selectedOptions[button.dataset.optionPosition];
      const isSelected = button.dataset.optionValue === selectedValue;
      button.classList.toggle("is-selected", isSelected);
      button.setAttribute("aria-pressed", isSelected ? "true" : "false");
    });
  }

  syncVariant() {
    if (!this.product) return;

    this.updateUnavailableOptions();
    this.state.selectedVariant = this.findMatchingVariant();

    if (this.selectedColorLabel) {
      const colorOption = this.getColorOption();
      this.selectedColorLabel.textContent = this.state.selectedOptions[colorOption?.position] || "";
    }
    const image = this.getVariantImage(this.state.selectedVariant);

    this.updateImage(this.productImage, image);

    this.resetConfirmedSelection();

    const requiredPositions = this.product.options.map((option) => String(option.position));
    const hasAllOptions = requiredPositions.every((position) => this.state.selectedOptions[position]);
    const canConfirm = Boolean(this.state.redemption && hasAllOptions && this.state.selectedVariant?.available);

    if (this.confirmButton) {
      this.confirmButton.disabled = this.state.completedSteps[2] || !canConfirm;
    }

    if (this.state.redemption && hasAllOptions && !this.state.selectedVariant) {
      this.showMessage(
        this.productMessage,
        this.text("combinationUnavailableText", "This option combination is not available."),
        "error",
      );
    } else if (this.state.redemption && this.state.selectedVariant && !this.state.selectedVariant.available) {
      this.showMessage(
        this.productMessage,
        this.text("variantUnavailableText", "This variant is currently unavailable."),
        "error",
      );
    } else {
      this.clearMessage(this.productMessage);
    }
  }

  updateUnavailableOptions() {
    if (!this.product) return;

    const colorOption = this.product.options.find((option) => /colou?r/i.test(option.name)) || this.product.options[0];

    this.optionButtons.forEach((button) => {
      const position = button.dataset.optionPosition;
      const value = button.dataset.optionValue;
      const candidateOptions = { [position]: value };

      if (colorOption && position !== String(colorOption.position)) {
        const selectedColor = this.state.selectedOptions[String(colorOption.position)];

        if (selectedColor) {
          candidateOptions[String(colorOption.position)] = selectedColor;
        }
      }

      const hasAvailableVariant = this.product.variants.some((variant) => {
        return variant.available && this.variantMatchesOptions(variant, candidateOptions);
      });

      button.classList.toggle("is-unavailable", !hasAvailableVariant);
    });
  }

  findMatchingVariant() {
    return (
      this.product.variants.find((variant) => this.variantMatchesOptions(variant, this.state.selectedOptions)) || null
    );
  }

  variantMatchesOptions(variant, options) {
    return Object.entries(options).every(([position, value]) => {
      return variant[`option${position}`] === value;
    });
  }

  getVariantImage(variant) {
    if (variant?.featuredImage?.src) return variant.featuredImage;
    const colorVariantImage = this.getVariantImageBySelectedColor();
    if (colorVariantImage?.src) return colorVariantImage;
    const colorImage = this.getImageBySelectedColor();
    if (colorImage?.src) return colorImage;
    const colorImageByIndex = this.getImageBySelectedColorIndex();
    if (colorImageByIndex?.src) return colorImageByIndex;

    return this.product.featuredImage;
  }

  getColorOption() {
    return this.product.options.find((option) => /colou?r/i.test(option.name)) || this.product.options[0];
  }

  getImageBySelectedColor() {
    const colorOption = this.getColorOption();
    const selectedColor = colorOption && this.state.selectedOptions[String(colorOption.position)];
    if (!selectedColor || !this.product.images?.length) return null;

    const colorToken = this.normalizeImageToken(selectedColor);
    const colorWords = String(selectedColor)
      .toLowerCase()
      .split(/[^a-z0-9]+/)
      .filter(Boolean);

    const scoredImages = this.product.images
      .map((image) => {
        const imageToken = this.normalizeImageToken(`${image.alt || ""} ${image.src || ""}`);
        const score = imageToken.includes(colorToken)
          ? 3
          : colorWords.length > 1 && colorWords.every((word) => imageToken.includes(word))
            ? 2
            : colorWords.some((word) => imageToken.includes(word))
              ? 1
              : 0;

        return { image, score };
      })
      .filter((item) => item.score > 0)
      .sort((a, b) => b.score - a.score);

    if (scoredImages[0]?.image) return scoredImages[0].image;

    return this.getImageBySelectedColorIndex();
  }

  /** Last resort only: `product.images` order is not guaranteed to match color option order in Shopify. */
  getImageBySelectedColorIndex() {
    const colorOption = this.getColorOption();
    const selectedColor = colorOption && this.state.selectedOptions[String(colorOption.position)];
    if (!selectedColor || !this.product.images?.length) return null;

    const colorIndex = colorOption.values.findIndex((value) => value === selectedColor);
    return colorIndex >= 0 ? this.product.images[colorIndex] || null : null;
  }

  getVariantImageBySelectedColor() {
    const colorOption = this.getColorOption();
    const selectedColor = colorOption && this.state.selectedOptions[String(colorOption.position)];
    if (!selectedColor) return null;

    const colorVariant = this.product.variants.find((item) => {
      return item[`option${colorOption.position}`] === selectedColor && item.featuredImage?.src;
    });

    return colorVariant?.featuredImage || null;
  }

  normalizeImageToken(value) {
    return String(value || "")
      .toLowerCase()
      .replace(/%20/g, " ")
      .replace(/[^a-z0-9]+/g, "");
  }

  updateImage(imageElement, image) {
    if (!imageElement || !image?.src) return;

    imageElement.removeAttribute("srcset");
    imageElement.removeAttribute("sizes");
    imageElement.src = image.src;
    imageElement.alt = image.alt || this.product.title;
  }

  validateFields() {
    const serialNumber = this.inputs.serialNumber?.value.trim() || "";
    const redemptionCode = this.inputs.redemptionCode?.value.trim() || "";
    const email = this.inputs.email?.value.trim() || "";
    let valid = true;

    valid =
      this.setFieldError(
        "serial_number",
        serialNumber ? "" : this.text("serialRequiredText", "Please enter your serial number."),
      ) && valid;
    valid =
      this.setFieldError(
        "redemption_code",
        redemptionCode ? "" : this.text("codeRequiredText", "Please enter your redemption code."),
      ) && valid;
    valid =
      this.setFieldError(
        "email",
        this.isValidEmail(email) ? "" : this.text("emailInvalidText", "Please enter a valid email address."),
      ) && valid;

    if (valid) {
      this.state.fields = { serialNumber, redemptionCode, email };
    }

    return valid;
  }

  isVerifyFormValid() {
    const serialNumber = this.inputs.serialNumber?.value.trim() || "";
    const redemptionCode = this.inputs.redemptionCode?.value.trim() || "";
    const email = this.inputs.email?.value.trim() || "";

    return Boolean(serialNumber && redemptionCode && this.isValidEmail(email));
  }

  updateVerifyButtonState() {
    if (!this.verifyButton || this.state.redemption) return;

    if (this.verifyButton.getAttribute("aria-busy") === "true") {
      this.verifyButton.disabled = true;
      return;
    }

    this.verifyButton.disabled = !this.state.productReady || !this.isVerifyFormValid();
  }

  setFieldError(name, message) {
    const error = this.querySelector(`[data-error-for="${name}"]`);
    const field = error?.closest(".gift-redemption-flow__field");
    field?.classList.toggle("has-error", Boolean(message));
    if (error) error.textContent = message;
    return !message;
  }

  clearFieldError(name) {
    this.setFieldError(name, "");
  }

  validateFieldOnBlur(key) {
    const fieldName = this.inputKeyToFieldName(key);
    const value = this.inputs[key]?.value.trim() || "";

    if (!value) {
      this.clearFieldError(fieldName);
      return;
    }

    if (key === "email" && !this.isValidEmail(value)) {
      this.setFieldError("email", this.text("emailInvalidText", "Please enter a valid email address."));
      return;
    }

    this.clearFieldError(fieldName);
  }

  inputKeyToFieldName(key) {
    const map = {
      serialNumber: "serial_number",
      redemptionCode: "redemption_code",
      email: "email",
    };

    return map[key] || key;
  }

  isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  buildGiftCardPayload(options = {}) {
    const payload = {
      giftCardSn: this.state.fields.serialNumber,
      giftCardCode: this.state.fields.redemptionCode,
      website: window?.ShopifyInfo?.webSite || "GB",
    };

    if (options.includeEmail !== false) {
      payload.giftCardEmail = this.state.fields.email;
    }

    if (options.includeProductGeneration) {
      const productGeneration = Number(this.dataset.productGeneration);
      if (productGeneration) payload.productGeneration = productGeneration;
    }

    return payload;
  }

  async verifyRedemption() {
    if (!this.state.productReady || !this.product) {
      this.showMessage(
        this.redemptionMessage,
        this.text(
          "productMissingText",
          "Product information is missing. Please check the page URL or configure a fallback product.",
        ),
        "error",
      );
      return;
    }

    if (!this.validateFields()) return;

    this.clearMessage(this.redemptionMessage);
    this.verifyButton.classList.remove("is-verified");
    this.cancelButton.hidden = true;
    this.setBusy(this.verifyButton, true);

    try {
      const result = await this.sendOmsRequest(
        this.dataset.verifyEndpoint,
        this.buildGiftCardPayload({ includeProductGeneration: true }),
      );

      if (result?.code === 50018 || result?.result === false) {
        this.state.redemption = null;
        this.state.completedSteps[1] = false;
        this.cacheProgress({ verified: false, confirmed: false });
        this.lockStep(2);
        this.showProductMismatchMessage(result);
        return;
      }

      this.applyVerifiedState(result || {});
      this.cacheProgress({ verified: true, confirmed: false });
      this.scrollToStep(2);
      this.syncVariant();
    } catch (error) {
      this.state.completedSteps[1] = false;
      this.showMessage(this.redemptionMessage, error.message || "Verification failed. Please try again.", "error");
    } finally {
      this.setBusy(this.verifyButton, false);
    }
  }

  applyVerifiedState(redemption) {
    this.state.redemption = redemption || {};
    this.verifyButton.querySelector("span").textContent =
      this.getAttribute("data-verified-text") || this.verifyButton.dataset.verifiedText || "Verified";
    this.verifyButton.classList.add("is-verified");
    this.verifyButton.disabled = true;
    this.cancelButton.hidden = false;
    this.inputs.serialNumber.disabled = true;
    this.inputs.redemptionCode.disabled = true;
    this.inputs.email.disabled = true;
    this.clearMessage(this.redemptionMessage);
    this.state.completedSteps[1] = true;
    this.clearMessage(this.productMessage);
    this.unlockStep(2);
  }

  showProductMismatchMessage(result) {
    const redirectUrl = this.getProductRedirectUrl(result);
    const apiMsg = [result?.msg, result?.message].find((s) => typeof s === "string" && s.trim());
    const messageText =
      (apiMsg && apiMsg.trim()) ||
      this.text(
        "productMismatchText",
        "The redeemed product does not match the current product. Please switch to the correct product.",
      );

    if (!redirectUrl) {
      this.showMessage(this.redemptionMessage, messageText, "error");
      return;
    }

    this.showHtmlMessage(
      this.redemptionMessage,
      `${this.escapeHtml(messageText)} <a href="${this.escapeAttribute(redirectUrl)}">${this.escapeHtml(this.text("productMismatchLinkText", "Go to correct product"))}</a>`,
      "error",
    );
  }

  getProductRedirectUrl(result) {
    const url = new URL(this.getRedeemPathByGeneration(result), window.location.origin);
    url.search = window.location.search;
    url.hash = window.location.hash;
    this.addGiftCardParamsToUrl(url);
    if (Number(result?.code) === 50018) {
      this.appendRegionSelectorSkipParams(url);
    }

    return url.toString();
  }

  /** 与 region-selector 跳过弹窗逻辑一致：为 URL 附加配置的 query 参数（值 true） */
  appendRegionSelectorSkipParams(url) {
    const skipParamNames = (this.dataset.regionSelectorSkipParams || "")
      .split(",")
      .map((name) => name.trim())
      .filter(Boolean);

    skipParamNames.forEach((name) => {
      if (name) url.searchParams.set(name, "true");
    });
  }

  addGiftCardParamsToUrl(url) {
    url.searchParams.set("giftCardSn", this.state.fields.serialNumber);
    url.searchParams.set("giftCardCode", this.state.fields.redemptionCode);
    url.searchParams.set("giftCardEmail", this.state.fields.email);
  }

  getRedeemPathByGeneration(result) {
    const product = result?.product || result?.productInfo || result?.product_info;
    const productGeneration = String(
      result?.productGeneration ||
        result?.product_generation ||
        product?.productGeneration ||
        product?.product_generation ||
        this.dataset.productGeneration ||
        "",
    );
    const pathMap = {
      1: "/pages/redeem-gen-2-air",
      2: "/pages/redeem",
    };

    return pathMap[productGeneration] || "/redeem";
  }

  async cancelRedemption() {
    this.setBusy(this.cancelButton, true);
    this.clearMessage(this.redemptionMessage);

    try {
      const result = await this.sendOmsRequest(
        this.dataset.unbindEndpoint,
        this.buildGiftCardPayload({ includeEmail: false }),
      );
      if (result?.result === false) throw new Error(result?.msg || "Cancel redemption failed. Please try again.");
    } catch (error) {
      const message = error.message || "Cancel redemption failed. Please try again.";
      this.showToast(message, "error");
      this.setBusy(this.cancelButton, false);
    } finally {
      this.stripGiftCardUrlParams();
      this.clearCache();
      window.location.reload();
    }
  }

  confirmSelection() {
    if (!this.state.selectedVariant) return;
    if (!this.state.selectedVariant.available) {
      this.syncVariant();
      return;
    }
    if (!this.state.completedSteps[1]) return;

    this.confirmButton.classList.remove("is-confirmed");
    this.setBusy(this.confirmButton, true);
    this.clearMessage(this.productMessage);

    this.state.discountCode = "";
    this.applyConfirmedState();
    this.cacheProgress({ confirmed: true });
    this.scrollToStep(3);
    this.setBusy(this.confirmButton, false);
  }

  applyConfirmedState() {
    this.state.checkoutVariantId = this.state.selectedVariant.id;
    this.state.completedSteps[2] = true;
    this.confirmButton.classList.add("is-confirmed");
    this.confirmButton.disabled = true;
    this.confirmButton.querySelector("span").textContent =
      this.getAttribute("data-confirmed-text") || this.confirmButton.dataset.confirmedText || "Confirmed";
    this.updateSummary();
    this.unlockStep(3);
  }

  updateSummary() {
    const variant = this.state.selectedVariant;
    const image = this.getVariantImage(variant);
    const variantTitle = variant?.title ? variant.title.replace(/\s*\/\s*/g, " - ") : "";

    if (this.summary.title)
      this.summary.title.textContent = [this.product.title, variantTitle].filter(Boolean).join(" - ");
    if (this.summary.serial) this.summary.serial.textContent = this.state.fields.serialNumber;
    if (this.summary.code) this.summary.code.textContent = this.getMaskedCode();
    if (this.summary.email) this.summary.email.textContent = this.state.fields.email;
    this.updateImage(this.summaryImage, image);

    if (this.checkoutButton) {
      this.checkoutButton.disabled = !this.termsCheckbox?.checked || !this.state.checkoutVariantId;
    }
  }

  toggleCodeVisibility() {
    this.state.codeVisible = !this.state.codeVisible;
    if (this.codeToggle) {
      this.codeToggle.setAttribute("aria-pressed", this.state.codeVisible ? "true" : "false");
    }
    if (this.summary.code) {
      this.summary.code.textContent = this.getMaskedCode();
    }
  }

  getMaskedCode() {
    const code = this.state.fields.redemptionCode || "";
    if (this.state.codeVisible) return code;
    return "*".repeat(code.length);
  }

  async goToCheckout() {
    if (!this.state.checkoutVariantId || !this.termsCheckbox?.checked) return;

    this.clearMessage(this.checkoutMessage);
    this.setBusy(this.checkoutButton, true);

    try {
      const result = await this.sendOmsRequest(this.dataset.discountEndpoint, this.buildGiftCardPayload());
      this.state.discountCode = result?.couponCode || result?.discountCode || result?.exchangeCouponCode || "";
      this.state.checkoutVariantId = result?.variantId || result?.variant_id || this.state.checkoutVariantId;

      const shopDomain = window.Shopify?.shop || window.location.hostname;
      let cartPath = `/cart/${this.state.checkoutVariantId}:1`;
      const params = new URLSearchParams();

      if (this.state.discountCode) {
        params.set("discount", this.state.discountCode);
      }

      if (this.state.fields.email) {
        params.set("checkout[email]", this.state.fields.email);
      }

      const queryString = params.toString();
      if (queryString) {
        cartPath += `?${queryString}`;
      }

      this.clearCache();
      this.clearInputFields();
      this.markLeavingForCheckout();
      window.location.href = `https://${shopDomain}${cartPath}`;
    } catch (error) {
      this.showMessage(this.checkoutMessage, error.message || "Failed to get discount. Please try again.", "error");
      this.setBusy(this.checkoutButton, false);
      this.checkoutButton.disabled = !this.termsCheckbox?.checked || !this.state.checkoutVariantId;
    }
  }

  unlockStep(stepNumber) {
    this.state.currentStep = Math.max(this.state.currentStep, stepNumber);
    this.querySelectorAll("[data-step]").forEach((step) => {
      const index = Number(step.dataset.step);
      step.classList.toggle("is-disabled", index > this.state.currentStep);
      step.classList.toggle("is-active", index === this.state.currentStep);
    });
    this.updateProgressLine();
  }

  lockStep(stepNumber) {
    this.state.currentStep = Math.min(this.state.currentStep, stepNumber - 1);
    this.querySelectorAll("[data-step]").forEach((step) => {
      const index = Number(step.dataset.step);
      step.classList.toggle("is-disabled", index >= stepNumber);
      step.classList.toggle("is-active", index === this.state.currentStep);
    });
    this.updateProgressLine();
  }

  updateProgressLine() {
    const activeStep = this.querySelector(`[data-step="${this.state.currentStep}"]`);
    const stepsLine = this.querySelector(".gift-redemption-flow__steps-line");
    const inner = this.querySelector(".gift-redemption-flow__inner");
    if (!activeStep || !stepsLine || !inner) return;

    const innerRect = inner.getBoundingClientRect();
    const stepHeading = activeStep.querySelector(".gift-redemption-flow__step-heading");
    const stepRect = (stepHeading || activeStep).getBoundingClientRect();
    const progressHeight = stepRect.bottom - innerRect.top;

    this.style.setProperty("--grf-progress-height", `${Math.max(progressHeight, 38)}px`);
  }

  scrollToStep(stepNumber) {
    if (stepNumber > 1 && !this.state.completedSteps[stepNumber - 1]) return;

    window.requestAnimationFrame(() => {
      this.querySelector(`[data-step="${stepNumber}"]`)?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  }

  setBusy(button, busy) {
    if (!button) return;

    const label = button.querySelector("span");

    if (busy) {
      if (button === this.verifyButton && label) {
        label.textContent = this.text("verifyingText", "Verifying...");
      }
      if (button === this.confirmButton && label) {
        label.textContent = this.text("confirmingText", "Confirming...");
      }
      button.setAttribute("aria-busy", "true");
      button.disabled = true;
    } else {
      button.removeAttribute("aria-busy");
      if (button === this.verifyButton && label && !this.state.redemption) {
        label.textContent = button.dataset.defaultText || "Verify";
      }
      if (button === this.confirmButton && label && !this.state.completedSteps[2]) {
        label.textContent = button.dataset.defaultText || "Confirm";
      }
      if (button !== this.verifyButton || !this.state.redemption) {
        button.disabled = false;
      }
      this.syncVariant();
      if (button === this.verifyButton) {
        this.updateVerifyButtonState();
      }
      if (button === this.confirmButton && this.state.completedSteps[2]) {
        button.disabled = true;
      }
    }
  }

  resetConfirmedSelection() {
    const selectedVariantId = this.state.selectedVariant?.id ? String(this.state.selectedVariant.id) : "";
    const keepConfirmed =
      this.state.completedSteps[2] &&
      this.state.selectedVariant?.available &&
      selectedVariantId &&
      String(this.state.checkoutVariantId) === selectedVariantId;
    if (keepConfirmed) return;
    if (!this.state.completedSteps[2] && !this.state.checkoutVariantId) return;

    this.state.completedSteps[2] = false;
    this.state.checkoutVariantId = "";
    this.state.discountCode = "";
    this.cacheProgress({ confirmed: false });
    this.confirmButton?.classList.remove("is-confirmed");

    const confirmLabel = this.confirmButton?.querySelector("span");
    if (confirmLabel) {
      confirmLabel.textContent = this.confirmButton.dataset.defaultText || "Confirm";
    }

    if (this.checkoutButton) {
      this.checkoutButton.disabled = true;
      this.checkoutButton.removeAttribute("aria-busy");
    }
    this.resetCheckoutStep();
    this.lockStep(3);
  }

  resetCheckoutStep() {
    this.state.codeVisible = false;
    this.updateImage(this.summaryImage, this.product?.featuredImage);

    if (this.summary?.title) this.summary.title.textContent = this.product?.title || "";
    if (this.summary?.variantTitle) this.summary.variantTitle.textContent = "";
    if (this.summary?.serial) this.summary.serial.textContent = "";
    if (this.summary?.code) this.summary.code.textContent = "";
    if (this.summary?.email) this.summary.email.textContent = "";

    const termsCheckbox = this.querySelector("[data-terms-checkbox]");
    if (termsCheckbox) {
      termsCheckbox.checked = false;
      termsCheckbox.dispatchEvent(new Event("change", { bubbles: true }));
    }
    this.termsCheckbox = termsCheckbox || this.termsCheckbox;

    if (this.codeToggle) this.codeToggle.setAttribute("aria-pressed", "false");

    const checkoutButton = this.querySelector("[data-checkout-button]");
    if (checkoutButton) {
      checkoutButton.removeAttribute("aria-busy");
      checkoutButton.disabled = true;
    }
    this.checkoutButton = checkoutButton || this.checkoutButton;
    this.clearMessage(this.checkoutMessage);
  }

  showMessage(element, message, type = "info") {
    if (!element) return;

    element.hidden = false;
    element.textContent = message;
    element.classList.toggle("is-error", type === "error");
    element.classList.toggle("is-success", type === "success");
  }

  showHtmlMessage(element, html, type = "info") {
    if (!element) return;

    element.hidden = false;
    element.innerHTML = html;
    element.classList.toggle("is-error", type === "error");
    element.classList.toggle("is-success", type === "success");
  }

  showToast(message, type = "info") {
    if (window.Toast?.show) {
      window.Toast.show(message, type);
      return;
    }

    const toast = document.createElement("div");
    toast.className = `gift-redemption-flow__toast is-${type}`;
    toast.textContent = message;
    document.body.appendChild(toast);
    window.requestAnimationFrame(() => toast.classList.add("is-visible"));
    window.setTimeout(() => {
      toast.classList.remove("is-visible");
      toast.addEventListener("transitionend", () => toast.remove(), { once: true });
    }, 3000);
  }

  clearMessage(element) {
    if (!element) return;

    element.hidden = true;
    element.textContent = "";
    element.classList.remove("is-error", "is-success");
  }

  async sendOmsRequest(path, formData) {
    await this.ensureCryptoJs();

    const payload = {
      client: {
        clientId: window?.ShopifyInfo?.clientId,
        languageCode: window?.ShopifyInfo?.language || "en",
      },
      payload: {
        businessData: this.encrypt(formData),
      },
    };

    const response = await fetch(this.buildOmsUrl(path), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(this.text("networkErrorText", "Network issue. Please try again later."));
    }

    const data = await response.json();
    const encryptedData = data?.payload?.resultData || data?.resultData;
    const responsePayload = encryptedData ? this.decrypt(encryptedData) : data?.payload || data?.data || data;
    const responseCode = Number(data.code);

    if (responseCode !== 0) {
      if (responseCode === 50018) {
        return {
          ...(responsePayload && typeof responsePayload === "object" ? responsePayload : {}),
          code: responseCode,
          msg: data.msg || data.message || "",
        };
      }
      throw new Error(data.msg || data.message || "Request failed.");
    }

    return responsePayload;
  }

  buildOmsUrl(path) {
    const apiDomain = window.location.hostname.includes("test") ? "dev.oms" : "oms";
    const normalizedPath = path?.startsWith("/") ? path : `/${path || ""}`;
    return `https://${apiDomain}.gdjztech.com${normalizedPath}`;
  }

  text(key, fallback) {
    return this.dataset[key] || fallback;
  }

  async ensureCryptoJs() {
    if (window.CryptoJS) return;

    await new Promise((resolve, reject) => {
      const existingScript = document.querySelector('script[data-gift-redemption-crypto="true"]');
      if (existingScript) {
        existingScript.addEventListener("load", resolve, { once: true });
        existingScript.addEventListener("error", reject, { once: true });
        return;
      }

      const script = document.createElement("script");
      script.src = CRYPTO_JS_CDN;
      script.defer = true;
      script.dataset.giftRedemptionCrypto = "true";
      script.addEventListener("load", resolve, { once: true });
      script.addEventListener("error", reject, { once: true });
      document.head.appendChild(script);
    });

    if (!window.CryptoJS) {
      throw new Error("Encryption library failed to load.");
    }
  }

  encrypt(data) {
    const { enc, AES, mode, pad } = window.CryptoJS;
    const aesKey = enc.Utf8.parse(AES_KEY);
    const jsonString = JSON.stringify(JSON.parse(JSON.stringify(data)));
    const base64Encoded = btoa(unescape(encodeURIComponent(jsonString)));
    const encrypted = AES.encrypt(base64Encoded, aesKey, {
      mode: mode.ECB,
      padding: pad.Pkcs7,
    });

    return encrypted.ciphertext.toString(enc.Base64);
  }

  decrypt(encryptedBase64) {
    const { enc, AES, mode, pad } = window.CryptoJS;
    const aesKey = enc.Utf8.parse(RESPONSE_KEY);
    const cipherParams = window.CryptoJS.lib.CipherParams.create({
      ciphertext: enc.Base64.parse(encryptedBase64),
    });
    const decrypted = AES.decrypt(cipherParams, aesKey, {
      mode: mode.ECB,
      padding: pad.Pkcs7,
    });
    const decryptedBase64 = decrypted.toString(enc.Utf8);
    const originalJson = decodeURIComponent(escape(atob(decryptedBase64)));

    return JSON.parse(originalJson);
  }
}

if (!window.customElements.get("gift-redemption-flow")) {
  window.customElements.define("gift-redemption-flow", GiftRedemptionFlow);
  GiftRedemptionFlow.ensureCheckoutReturnListener();
}
